import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { Database, ShieldAlert, CheckCircle2 } from 'lucide-react';

// --- ANIMATION VARIANTS ---
const containerVariants = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { staggerChildren: 0.05 }
  }
};

const tableRowVariants = {
  hidden: { opacity: 0, x: -10 },
  show: { 
    opacity: 1, 
    x: 0, 
    transition: { type: "spring", stiffness: 300, damping: 24 } 
  }
};

const modalVariants = {
  hidden: { opacity: 0, scale: 0.95, y: 10 },
  show: { 
    opacity: 1, 
    scale: 1, 
    y: 0, 
    transition: { type: "spring", duration: 0.3 } 
  },
  exit: { 
    opacity: 0, 
    scale: 0.95, 
    y: -10, 
    transition: { duration: 0.2 } 
  }
};

export default function AiInventory() {
  const { user, token, isImpersonating } = useAuth(); 
  const navigate = useNavigate();
  
  const [inventory, setInventory] = useState([]);
  const [loading, setLoading] = useState(true);

  // States for dynamic questions and answers
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [showAddModal, setShowAddModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const [questionnaireData, setQuestionnaireData] = useState(null);
  const [generatingQ, setGeneratingQ] = useState(false);

  // Unified Standardized Alert Modal
  const [alertModal, setAlertModal] = useState({ 
    show: false, 
    title: '', 
    message: '', 
    type: 'info' 
  });
  
  const [confirmModal, setConfirmModal] = useState({ 
    show: false, 
    toolId: null, 
    toolName: '' 
  });

  // Reverted formData to only track core details, removing hardcoded audit flags
  const [formData, setFormData] = useState({
    name: '', 
    vendor: '', 
    owner: '', 
    sanctioned: false
  });

  const fetchInventoryAndQuestions = async () => {
    if (!user || !user.org_id) return;
    try {
      setLoading(true);
      const baseURL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
      const authToken = token || localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${authToken}` };
      
      const params = { orgId: user.org_id };
      const [invRes, qRes] = await Promise.all([
        axios.get(`${baseURL}/api/inventory`, { headers, params }),
        axios.get(`${baseURL}/api/inventory/questions`, { headers, params })
      ]);

      setInventory(invRes.data || []);
      
      const sortedQuestions = (qRes.data || []).sort((a, b) => a.order_index - b.order_index);
      setQuestions(sortedQuestions);
      
    } catch (error) {
      console.error("Failed to fetch data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user?.org_id) {
      fetchInventoryAndQuestions();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.org_id, token]);

  const handleAddTool = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    const unanswered = questions.filter(q => !answers[q.id]);
    if (unanswered.length > 0) {
      setAlertModal({ 
        show: true, 
        title: 'Incomplete Assessment', 
        message: 'Please answer all assessment questions before saving.', 
        type: 'error' 
      });
      setIsSubmitting(false);
      return;
    }

    try {
      const baseURL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
      const authToken = token || localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${authToken}` };
      
      let mappedDataType = 'General Business Data';
      questions.forEach(q => {
        if (q.order_index === 1 && answers[q.id]) {
          mappedDataType = answers[q.id]; 
        }
      });
      
      // Removed hardcoded audit flags, passing only dynamic answers
      const payload = {
        orgId: user.org_id,
        tool_name: formData.name,
        vendor: formData.vendor,
        department: formData.owner,
        sanctioned: formData.sanctioned,
        data_types: [mappedDataType],
        assessment_answers: answers
      };

      await axios.post(`${baseURL}/api/inventory`, payload, { headers });
      
      setShowAddModal(false);
      setAlertModal({ 
        show: true, 
        title: 'Tool Registered', 
        message: `${formData.name} was successfully analyzed and added to your registry.`, 
        type: 'success' 
      });
      
      setFormData({ 
        name: '', 
        vendor: '', 
        owner: '', 
        sanctioned: false
      });
      setAnswers({});
      await fetchInventoryAndQuestions();
      
    } catch (error) {
      console.error("Failed to add tool:", error);
      setAlertModal({ 
        show: true, 
        title: 'Error', 
        message: error.response?.data?.error || "Authentication failed or server error.", 
        type: 'error' 
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const promptDeleteTool = (toolId, toolName) => {
    setConfirmModal({ show: true, toolId, toolName });
  };

  const executeDeleteTool = async () => {
    const { toolId, toolName } = confirmModal;
    setConfirmModal({ show: false, toolId: null, toolName: '' });
    
    try {
      const baseURL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
      const authToken = token || localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${authToken}` };
      
      await axios.delete(`${baseURL}/api/inventory/${toolId}`, { headers });
      setAlertModal({ 
        show: true, 
        title: 'Tool Deprecated', 
        message: `${toolName} has been successfully removed. Your corporate AI policy has been invalidated and must be regenerated.`, 
        type: 'success' 
      });
      await fetchInventoryAndQuestions();
    } catch (error) {
      console.error("Failed to delete tool:", error);
      setAlertModal({ 
        show: true, 
        title: 'Error', 
        message: "Failed to remove the tool. Please check your connection.", 
        type: 'error' 
      });
    }
  };

  const handleApproveTool = async (toolId) => {
    try {
      const baseURL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
      const authToken = token || localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${authToken}` };
      
      await axios.patch(`${baseURL}/api/inventory/${toolId}/approve`, {}, { headers });
      setAlertModal({ 
        show: true, 
        title: 'Tool Sanctioned', 
        message: 'Tool successfully approved. Your corporate AI policy has been invalidated and must be regenerated.', 
        type: 'success' 
      });
      await fetchInventoryAndQuestions();
    } catch (error) {
      console.error("Failed to approve tool:", error);
      setAlertModal({ 
        show: true, 
        title: 'Error', 
        message: "Failed to approve the tool.", 
        type: 'error' 
      });
    }
  };

  const handleGenerateQuestionnaire = async (toolId) => {
    setGeneratingQ(toolId);
    try {
      const baseURL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
      const authToken = token || localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${authToken}` };

      const res = await axios.get(`${baseURL}/api/inventory/${toolId}/questionnaire`, { headers });
      setQuestionnaireData(res.data);
    } catch (err) {
      console.error("Failed to generate questionnaire", err);
      if (err.response && err.response.status === 403) {
        setAlertModal({ 
          show: true, 
          title: '⭐ Premium Feature Unlocked at Tier 1', 
          message: err.response.data.error,
          type: 'info' 
        });
      } else {
        setAlertModal({ 
          show: true, 
          title: 'Generation Failed', 
          message: 'Failed to generate the questionnaire. Please try again or contact support.', 
          type: 'error' 
        });
      }
    }
    setGeneratingQ(false);
  };

  const copyQuestionnaireToClipboard = () => {
    if (!questionnaireData) return;
    
    let text = `Vendor Security & Compliance Questionnaire for ${questionnaireData.tool}\n\n`;
    questionnaireData.questions.forEach((q, i) => {
      text += `${i + 1}. [${q.category}] ${q.question}\n   (Context: ${q.why_it_matters})\n\n`;
    });
    
    navigator.clipboard.writeText(text);
    
    setAlertModal({
      show: true,
      title: 'Copied to Clipboard!',
      message: 'You can now paste this highly-targeted questionnaire into an email to the vendor.',
      type: 'success'
    });
  };

  const downloadQuestionnaire = () => {
    if (!questionnaireData) return;
    
    let text = `VENDOR SECURITY & COMPLIANCE QUESTIONNAIRE\n`;
    text += `Target System: ${questionnaireData.tool}\n`;
    text += `Vendor: ${questionnaireData.vendor}\n`;
    text += `Date Generated: ${new Date().toLocaleDateString()}\n`;
    text += `======================================================\n\n`;
    text += `Please provide detailed responses to the following compliance inquiries regarding your AI system:\n\n`;
    
    questionnaireData.questions.forEach((q, i) => {
      text += `QUESTION ${i + 1} [${q.category.toUpperCase()}]\n`;
      text += `${q.question}\n\n`;
      text += `[  ] Vendor Response:\n\n\n\n`;
      text += `------------------------------------------------------\n`;
    });

    const blob = new Blob([text], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${questionnaireData.tool.replace(/\s+/g, '_')}_Security_Questionnaire.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const getRiskBadge = (score) => {
    if (!score) {
      return <span className="text-slate-400 font-medium">N/A</span>;
    }
    if (score >= 7) {
      return <span className="px-3 py-1 bg-red-100 text-red-700 border border-red-200 font-bold rounded-lg text-xs shadow-sm">{score}/10 (High)</span>;
    }
    if (score >= 4) {
      return <span className="px-3 py-1 bg-amber-100 text-amber-700 border border-amber-200 font-bold rounded-lg text-xs shadow-sm">{score}/10 (Med)</span>;
    }
    return <span className="px-3 py-1 bg-emerald-100 text-emerald-700 border border-emerald-200 font-bold rounded-lg text-xs shadow-sm">{score}/10 (Low)</span>;
  };

  const getStatusBadge = (status) => {
    if (status === true || status === 'Approved') {
      return <span className="px-3 py-1 bg-indigo-100 text-indigo-700 font-bold rounded-full text-xs border border-indigo-200 shadow-sm">Approved</span>;
    }
    return <span className="px-3 py-1 bg-rose-100 text-rose-700 font-bold rounded-full text-xs border border-rose-200 shadow-sm">Unsanctioned / Shadow AI</span>;
  };

  return (
    <div className="p-8 relative">
      
      {/* ── ALERTS MODAL ── */}
      <AnimatePresence>
        {alertModal.show && (
          <motion.div 
            initial={{ opacity: 0 }} 
            animate={{ opacity: 1 }} 
            exit={{ opacity: 0 }} 
            className="fixed inset-0 z-[80] flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm"
          >
            <motion.div 
              variants={modalVariants} 
              initial="hidden" 
              animate="show" 
              exit="exit" 
              className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6 transform transition-all text-center"
            >
              <div className={`w-16 h-16 rounded-full flex items-center justify-center text-3xl mx-auto mb-4 ${
                alertModal.type === 'success' ? 'bg-emerald-100 text-emerald-600' : 
                alertModal.type === 'info' ? 'bg-blue-100 text-blue-600' : 'bg-red-100 text-red-600'
              }`}>
                {alertModal.type === 'success' ? '✓' : alertModal.type === 'info' ? 'ℹ' : '⚠'}
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2">{alertModal.title}</h3>
              <p className="text-slate-500 mb-6 text-sm leading-relaxed">{alertModal.message}</p>
              
              <div className="flex flex-col gap-2">
                {alertModal.title.includes('Premium') && (
                  <button 
                    onClick={() => {
                      setAlertModal({ show: false, title: '', message: '', type: 'info' });
                      navigate('/upgrade');
                    }} 
                    className="w-full px-4 py-2.5 bg-blue-600 text-white font-bold rounded-lg hover:bg-blue-700 transition-colors shadow-md"
                  >
                    Upgrade to Tier 1
                  </button>
                )}
                
                <button 
                  onClick={() => setAlertModal({ show: false, title: '', message: '', type: 'info' })} 
                  className="w-full px-4 py-2.5 bg-slate-900 text-white font-bold rounded-lg hover:bg-slate-800 transition-colors"
                >
                  {alertModal.title.includes('Premium') ? 'Maybe Later' : 'Acknowledge'}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── DELETE CONFIRMATION MODAL ── */}
      <AnimatePresence>
        {confirmModal.show && (
          <motion.div 
            initial={{ opacity: 0 }} 
            animate={{ opacity: 1 }} 
            exit={{ opacity: 0 }} 
            className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm"
          >
            <motion.div 
              variants={modalVariants} 
              initial="hidden" 
              animate="show" 
              exit="exit" 
              className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 transform transition-all text-center"
            >
              <div className="w-16 h-16 rounded-full bg-red-100 text-red-600 flex items-center justify-center text-3xl mx-auto mb-4">⚠</div>
              <h3 className="text-xl font-bold text-slate-900 mb-2">Remove Tool?</h3>
              <p className="text-slate-500 mb-6 text-sm leading-relaxed">
                Are you sure you want to deprecate <strong>{confirmModal.toolName}</strong>? This will permanently mark it as removed, invalidate your current AI policy, and reset your Risk Analysis snapshot.
              </p>
              <div className="flex gap-3">
                <button 
                  onClick={() => setConfirmModal({ show: false, toolId: null, toolName: '' })} 
                  className="flex-1 px-4 py-2.5 border border-slate-300 text-slate-700 font-bold rounded-lg hover:bg-slate-50 transition-colors"
                >
                  Cancel
                </button>
                <button 
                  onClick={executeDeleteTool} 
                  className="flex-1 px-4 py-2.5 bg-red-600 text-white font-bold rounded-lg hover:bg-red-700 transition-colors shadow-md"
                >
                  Yes, Remove Tool
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── ADD TOOL MODAL ── */}
      <AnimatePresence>
        {showAddModal && (
          <motion.div 
            initial={{ opacity: 0 }} 
            animate={{ opacity: 1 }} 
            exit={{ opacity: 0 }} 
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm overflow-hidden"
          >
            <motion.div 
              variants={modalVariants} 
              initial="hidden" 
              animate="show" 
              exit="exit" 
              className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col transform transition-all"
            >
              
              <div className="flex justify-between items-center p-6 border-b border-slate-100 bg-slate-50 rounded-t-2xl shrink-0">
                <div>
                  <h3 className="text-xl font-bold text-slate-900">Register New AI Tool</h3>
                  <p className="text-sm text-slate-500 mt-1">Complete your global compliance assessment.</p>
                </div>
                <button 
                  onClick={() => { setShowAddModal(false); setAnswers({}); }} 
                  className="text-slate-400 hover:text-slate-600 text-2xl leading-none"
                >
                  &times;
                </button>
              </div>
              
              <div className="p-6 overflow-y-auto">
                <form id="ai-tool-form" onSubmit={handleAddTool} className="space-y-6">
                  
                  {/* CORE DETAILS */}
                  <div className="bg-blue-50/50 p-5 rounded-xl border border-blue-100 space-y-4">
                    <h4 className="font-bold text-blue-900 text-sm uppercase tracking-wider mb-2">1. Core Details</h4>
                    
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-1">Tool Name *</label>
                      <input 
                        required 
                        type="text" 
                        placeholder="e.g., ChatGPT Enterprise" 
                        className="w-full border border-slate-300 rounded-lg px-3 py-2.5 focus:ring-2 focus:ring-blue-500 outline-none" 
                        value={formData.name} 
                        onChange={(e) => setFormData({...formData, name: e.target.value})} 
                      />
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-1">Vendor *</label>
                        <input 
                          required 
                          type="text" 
                          placeholder="e.g., OpenAI" 
                          className="w-full border border-slate-300 rounded-lg px-3 py-2.5 focus:ring-2 focus:ring-blue-500 outline-none" 
                          value={formData.vendor} 
                          onChange={(e) => setFormData({...formData, vendor: e.target.value})} 
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-1">Business Owner *</label>
                        <input 
                          required 
                          type="text" 
                          placeholder="e.g., Marketing Team" 
                          className="w-full border border-slate-300 rounded-lg px-3 py-2.5 focus:ring-2 focus:ring-blue-500 outline-none" 
                          value={formData.owner} 
                          onChange={(e) => setFormData({...formData, owner: e.target.value})} 
                        />
                      </div>
                    </div>

                    <label className="flex items-center gap-3 cursor-pointer mt-4 border-t border-blue-100 pt-4">
                      <input 
                        type="checkbox" 
                        checked={formData.sanctioned} 
                        onChange={(e) => setFormData({...formData, sanctioned: e.target.checked})} 
                        className="w-5 h-5 text-blue-600 rounded" 
                      />
                      <div>
                        <span className="block text-sm font-bold text-slate-800">Officially Approved Tool</span>
                        <span className="block text-xs text-slate-500">Check this box if IT/Security has explicitly authorized this tool.</span>
                      </div>
                    </label>
                  </div>

                  {/* INTERNAL RISK ASSESSMENT QUESTIONNAIRE */}
                  <div className="space-y-5">
                    <h4 className="font-bold text-blue-900 text-sm uppercase tracking-wider mb-2 border-b border-slate-100 pb-2">2. Dynamic Compliance Assessment</h4>
                    
                    {questions.length === 0 ? (
                      <p className="text-sm text-slate-500 italic">No assessment questions configured for your tier.</p>
                    ) : (
                      questions.map((q) => {
                        let parsedOptions = [];
                        if (Array.isArray(q.options)) {
                          parsedOptions = q.options;
                        } else if (typeof q.options === 'string') {
                          try { 
                            parsedOptions = JSON.parse(q.options); 
                          } catch (e) { 
                            parsedOptions = q.options.replace(/[\[\]"]/g, '').split(','); 
                          }
                        }

                        return (
                          <div key={q.id} className="bg-slate-50 p-4 rounded-xl border border-slate-200">
                            <label className="block text-sm font-semibold text-slate-800 mb-2">
                              {q.order_index}. {q.question_text} *
                            </label>
                            
                            {q.question_type === 'SINGLE_SELECT' && (
                              <select 
                                required 
                                className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-blue-500" 
                                value={answers[q.id] || ''} 
                                onChange={(e) => setAnswers({ ...answers, [q.id]: e.target.value })}
                              >
                                <option value="">-- Select an option --</option>
                                {parsedOptions.map((opt, idx) => (
                                  <option key={idx} value={opt?.trim ? opt.trim() : opt}>
                                    {opt?.trim ? opt.trim() : opt}
                                  </option>
                                ))}
                              </select>
                            )}
                            
                            {q.question_type === 'TEXT' && (
                              <input 
                                required 
                                type="text" 
                                className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-blue-500" 
                                value={answers[q.id] || ''} 
                                onChange={(e) => setAnswers({ ...answers, [q.id]: e.target.value })} 
                              />
                            )}
                          </div>
                        );
                      })
                    )}
                  </div>
                </form>
              </div>
              
              <div className="p-6 border-t border-slate-100 bg-white flex gap-3 rounded-b-2xl">
                <button 
                  type="button" 
                  onClick={() => { setShowAddModal(false); setAnswers({}); }} 
                  className="flex-1 px-4 py-3 border border-slate-300 text-slate-700 font-bold rounded-xl hover:bg-slate-50"
                >
                  Cancel
                </button>
                <button 
                  form="ai-tool-form" 
                  type="submit" 
                  disabled={isSubmitting} 
                  className="flex-1 px-4 py-3 bg-blue-600 text-white font-bold rounded-xl hover:bg-blue-700 disabled:opacity-50 flex justify-center items-center shadow-md"
                >
                  {isSubmitting ? 'Analysing Controls...' : 'Save & Map Controls'}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── VENDOR QUESTIONNAIRE MODAL ── */}
      <AnimatePresence>
        {questionnaireData && (
          <motion.div 
            initial={{ opacity: 0 }} 
            animate={{ opacity: 1 }} 
            exit={{ opacity: 0 }} 
            className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          >
            <motion.div 
              variants={modalVariants} 
              initial="hidden" 
              animate="show" 
              exit="exit" 
              className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
            >
              <div className="sticky top-0 bg-white border-b border-slate-100 p-6 flex justify-between items-center z-10">
                <div>
                  <h3 className="text-xl font-bold text-slate-800">Vendor Questionnaire</h3>
                  <p className="text-sm text-slate-500">Customized for {questionnaireData.tool} ({questionnaireData.vendor})</p>
                </div>
                <button 
                  onClick={() => setQuestionnaireData(null)} 
                  className="text-slate-400 hover:text-slate-600 text-2xl"
                >
                  &times;
                </button>
              </div>
              
              <div className="p-6 space-y-6 bg-slate-50">
                <div className="bg-blue-50 border border-blue-200 text-blue-800 text-sm p-4 rounded-xl shadow-sm">
                  <strong>💡 Tip:</strong> Copy these highly-targeted questions and email them directly to your vendor's security or support team. Their answers will serve as your formal compliance evidence.
                </div>

                <div className="space-y-4">
                  {questionnaireData.questions.map((q, idx) => (
                    <div key={idx} className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="bg-slate-100 text-slate-600 text-xs font-bold px-2 py-1 rounded border border-slate-200 uppercase tracking-wider">{q.category}</span>
                      </div>
                      <p className="text-slate-800 font-bold mb-3">{idx + 1}. {q.question}</p>
                      <div className="bg-slate-50 border-l-4 border-blue-400 p-3 rounded-r-lg">
                        <p className="text-xs text-slate-600"><strong className="text-slate-700">Internal Note (Why we ask):</strong> {q.why_it_matters}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="sticky bottom-0 bg-white border-t border-slate-100 p-6 flex justify-end gap-3 rounded-b-2xl">
                <button 
                  onClick={() => setQuestionnaireData(null)} 
                  className="px-5 py-2.5 text-sm font-bold text-slate-600 hover:bg-slate-50 border border-slate-200 rounded-lg"
                >
                  Close
                </button>
                
                <button 
                  onClick={downloadQuestionnaire} 
                  className="px-5 py-2.5 text-sm font-bold text-slate-700 bg-slate-100 hover:bg-slate-200 border border-slate-300 rounded-lg shadow-sm flex items-center gap-2"
                >
                  <span>📥</span> Download File
                </button>
                <button 
                  onClick={copyQuestionnaireToClipboard} 
                  className="px-5 py-2.5 text-sm font-bold text-white bg-blue-600 hover:bg-blue-700 rounded-lg shadow-sm flex items-center gap-2"
                >
                  <span>📋</span> Copy to Clipboard
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── HEADER ── */}
      <div className="flex justify-between items-end mb-8">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">AI Inventory Register</h2>
          <p className="text-slate-500 mt-1">Manage all AI deployments and track compliance.</p>
        </div>
        <motion.button 
          whileHover={{ scale: 1.02 }} 
          whileTap={{ scale: 0.98 }} 
          onClick={() => setShowAddModal(true)} 
          disabled={isImpersonating} 
          className={`font-medium px-4 py-2 rounded-lg text-sm transition shadow-sm ${
            isImpersonating ? 'bg-slate-200 text-slate-400 cursor-not-allowed' : 'bg-slate-900 hover:bg-slate-800 text-white'
          }`}
        >
          {isImpersonating ? '👁 Read-Only' : '+ Add AI Tool'}
        </motion.button>
      </div>

      {/* ── INVENTORY TABLE ── */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-20 text-center text-slate-500 flex flex-col items-center">
            <motion.div 
              animate={{ rotate: 360 }} 
              transition={{ repeat: Infinity, duration: 1, ease: "linear" }} 
              className="h-8 w-8 border-4 border-blue-200 border-t-blue-600 rounded-full mb-4" 
            />
            Loading inventory...
          </div>
        ) : inventory.length === 0 ? (
          <div className="p-20 text-center">
            <div className="text-6xl mb-6">🗂️</div>
            <h3 className="text-2xl font-bold text-slate-800 mb-3">Your Inventory is Empty</h3>
            <p className="text-slate-500 mb-8 max-w-md mx-auto">Add your first tool to map its compliance controls.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200 text-sm font-bold text-slate-600 uppercase tracking-wider">
                  <th className="px-6 py-4">AI Model / Tool</th>
                  <th className="px-6 py-4">Vendor</th>
                  <th className="px-6 py-4">Audit Coverage</th>
                  <th className="px-6 py-4">Status</th>
                  <th className="px-6 py-4">Risk Score</th>
                  <th className="px-6 py-4 text-right">Actions</th>
                </tr>
              </thead>
              <motion.tbody 
                variants={containerVariants} 
                initial="hidden" 
                animate="show" 
                className="divide-y divide-slate-100"
              >
                {inventory.map((item) => {
                   // AUDIT READINESS LOGIC: Check if legal coverage is missing dynamically from backend evaluation
                   const hasViolations = item.framework_controls && item.framework_controls.length > 0 && item.framework_controls.some(c => c.status === 'Failed');
                   
                   return (
                     <motion.tr variants={tableRowVariants} key={item.id} className="hover:bg-slate-50/80 transition-colors group">
                       <td className="px-6 py-5">
                         <p className="font-bold text-slate-900 text-base">{item.name || item.tool_name}</p>
                       </td>
                       
                       <td className="px-6 py-5 text-sm font-semibold text-slate-700">{item.vendor || 'Internal'}</td>
                       
                       <td className="px-6 py-5">
                           {hasViolations ? (
                               <span className="flex w-fit items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-red-700 bg-red-50 border border-red-200 px-2.5 py-1 rounded shadow-sm">
                                   <ShieldAlert className="w-3 h-3" /> Action Required
                               </span>
                           ) : (
                               <span className="flex w-fit items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-emerald-700 bg-emerald-50 border border-emerald-200 px-2.5 py-1 rounded shadow-sm">
                                   <CheckCircle2 className="w-3 h-3" /> Audit Ready
                               </span>
                           )}
                       </td>
                       
                       <td className="px-6 py-5">{getStatusBadge(item.sanctioned)}</td>
                       <td className="px-6 py-5">{getRiskBadge(item.risk_score)}</td>
                       
                       <td className="px-6 py-5 text-right space-x-2 whitespace-nowrap">
                         {!isImpersonating && (
                           <>
                             <button 
                               onClick={() => handleGenerateQuestionnaire(item.id)} 
                               disabled={generatingQ === item.id} 
                               className="text-xs px-3 py-2 border rounded-lg font-semibold transition-colors shadow-sm mr-2 bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100"
                             >
                               {generatingQ === item.id ? '⏳...' : '📋 DPA Eval'}
                             </button>

                             {item.sanctioned !== true && (
                                <button onClick={() => handleApproveTool(item.id)} className="text-indigo-700 bg-indigo-50 border border-indigo-200 shadow-sm hover:bg-indigo-100 text-sm font-bold px-4 py-2 rounded-lg transition-all mr-2">
                                    Approve
                                </button>
                             )}
                             
                             <button 
                               onClick={() => promptDeleteTool(item.id, item.name || item.tool_name)} 
                               className="text-red-600 hover:text-red-800 bg-red-50 hover:bg-red-100 font-medium px-3 py-2 rounded-lg text-sm border border-red-100 shadow-sm ml-2 transition-colors"
                             >
                               Remove
                             </button>
                           </>
                         )}
                       </td>
                     </motion.tr>
                   );
                })}
              </motion.tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}